<template>
  <div :data-widget-title="content.optionalTitle?.text">
    <SliceTitle class="title my-10 lg:text-center" :value="content.optionalTitle" />
    <div class="box-container text-center p-3 sm:p-8">
      <div class="scroller relative">
        <table class="w-full">
          <thead>
            <tr>
              <th />
              <th v-for="header in table[0].cols" :key="header.to">
                <span
                  class="whitespace-nowrap inline-flex items-center border-b-3"
                  :class="selectedCell.to === header.to ? 'border-brand-600' : 'border-transparent'"
                >
                  <span class="flag rounded-4" :class="`flag-${header.to.toLowerCase()}`" />
                  <span>{{ header.to }}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, r) in table" :key="r">
              <th>
                <span
                  class="whitespace-nowrap inline-flex items-center border-b-3"
                  :class="
                    selectedCell.from === row.from ? 'border-brand-600' : 'border-transparent'
                  "
                >
                  <span class="flag rounded-4" :class="`flag-${row.from.toLowerCase()}`" />
                  <span>1 {{ row.from }}</span>
                </span>
              </th>
              <td v-for="(cell, l) in row.cols" :key="l" @mouseover="onMouseOver(row, cell)">
                <a
                  v-if="row.from !== cell.to"
                  :href="getUrl(row.from, cell.to)"
                  class="block p-3 sm:py-5"
                  :class="{
                    'font-bold': selectedCell.from === row.from && selectedCell.to === cell.to,
                  }"
                >
                  <span class="underline">{{ getRate(cell.rate) }}</span>
                </a>
                <span v-else class="block bg-gray-50 p-3 sm:py-5">&nbsp;</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="text-right text-12 text-gray-500 py-2">
        {{ $t('source') }} <XeLink /> | {{ $t('last_updated', { ago: '' }) }}
        <NuxtTime :datetime="content.ratesTable.lastUpdated" :locale="$lang" relative />
      </div>
      <a
        v-if="content.optionalCta"
        :href="content.optionalCta?.link ?? undefined"
        class="block link mt-6 mb-2"
      >
        {{ content.optionalCta.label }}
      </a>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { GQLCurrencyTableSlice } from './generated/currency-table'

type Row = GQLCurrencyTableSlice['ratesTable']['rows'][number]
type Cell = Row['cols'][number]

const props = defineProps<{ content: GQLCurrencyTableSlice }>()

const liveRateFromTo = useLiveExchangeRate(
  props.content.ratesTable.rows[0].from,
  props.content.ratesTable.rows[1].from,
)
const liveRateToFrom = useLiveExchangeRate(
  props.content.ratesTable.rows[1].from,
  props.content.ratesTable.rows[0].from,
)
const table = computed(() => {
  if (!liveRateFromTo.value || !liveRateToFrom.value) {
    return props.content.ratesTable.rows
  }
  const rows = [...props.content.ratesTable.rows]
  rows[0].cols[1].rate = liveRateFromTo.value
  rows[1].cols[0].rate = liveRateToFrom.value
  return rows
})

const { $lang } = useNuxtApp()

const selectedCell = reactive({
  from: props.content.ratesTable.rows[0].from,
  to: props.content.ratesTable.rows[0].cols[1].to,
})

function getUrl(from: string, to: string) {
  return getCurrencyUrl({ currencyFrom: { iso: from }, currencyTo: { iso: to } })
}

function onMouseOver(row: Row, cell: Cell) {
  selectedCell.from = row.from
  selectedCell.to = cell.to
}

function getRate(rate: number) {
  return formatRate(rate, $lang)
}
</script>

<style scoped>
th {
  @apply text-center p-3 sm:py-5;
  min-width: 125px;
}

td {
  @apply border border-gray-300 cursor-pointer;
}

tr th:first-child {
  @apply sticky left-0 top-auto bg-white;
}

.scroller {
  @apply overflow-x-auto;
  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(212, 212, 212) rgb(248, 248, 248);
}

.scroller::-webkit-scrollbar {
  @apply w-2 h-2;
}

.scroller::-webkit-scrollbar-corner {
  @apply bg-transparent;
}

.scroller::-webkit-scrollbar-thumb {
  @apply bg-gray-300 rounded-10;
}

.scroller::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-400;
}

.scroller::-webkit-scrollbar-track {
  @apply bg-gray-50 bg-clip-content;
  margin-left: 125px; /* gap to hide scroll from 1st column */
}

@screen lg {
  .scroller::-webkit-scrollbar {
    @apply hidden;
  }
}
</style>
